/**
 * widgets/NotificationBell.ts · 顶 NAV 红 badge 通知铃铛
 *
 * 替代 v200.x 散乱的通知 UI:
 *   - 顶 NAV 🔔 icon + 红 badge (未读数)
 *   - polling 30s 自动刷新
 *   - 点击 → 跳 #notifications page
 *   - 0 未读时 badge 隐藏
 */

import type { User } from '@core/auth';
import { getUnreadCount } from '@services/notify';

const POLL_INTERVAL_MS = 30_000;
let _pollTimer: ReturnType<typeof setInterval> | null = null;
// 保留最新 user 给 refreshBell() 调用 (跨 widget 实例)
let _currentUser: User | null = null;

export function renderNotificationBell(user: User): HTMLElement {
  _currentUser = user;
  const wrap = document.createElement('button');
  wrap.id = 'tn-notif-bell';
  wrap.className = 'relative p-1 rounded hover:bg-gray-700 transition';
  wrap.title = '通知 / Thông báo';
  wrap.onclick = () => { window.location.hash = '#notifications'; };
  wrap.innerHTML = `
    <span class="text-lg">🔔</span>
    <span class="tn-notif-badge hidden absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"></span>
  `;
  // badge 用 closure 引用 (DOM 未 attach 也能拿到 · 比 getElementById 稳)
  const badge = wrap.querySelector<HTMLSpanElement>('.tn-notif-badge')!;

  // 异步首刷 (等 DOM cycle + db init) + polling
  setTimeout(() => doRefresh(badge, user), 100);
  setTimeout(() => doRefresh(badge, user), 1000);  // 兜底 · db init 慢时
  if (_pollTimer) clearInterval(_pollTimer);
  _pollTimer = setInterval(() => doRefresh(badge, user), POLL_INTERVAL_MS);

  // 用户切换 + 路由变化 + 通知页关闭 时刷新
  document.addEventListener('tn-user-login', () => doRefresh(badge, user));
  window.addEventListener('hashchange', () => doRefresh(badge, user));
  // 暴露 badge 供 refreshBell() 使用
  (wrap as any)._tnBadge = badge;

  return wrap;
}

function doRefresh(badge: HTMLSpanElement, user: User): void {
  try {
    const count = getUnreadCount(user.role, user.code);
    if (count > 0) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.classList.remove('hidden');
    } else {
      badge.textContent = '';
      badge.classList.add('hidden');
    }
  } catch {
    /* DB 未 init · 静默 */
  }
}

/** 手动触发刷新 · workflow / markRead 后调 */
export function refreshBell(): void {
  // 找当前 bell widget (TopNav 内)
  const bell = document.getElementById('tn-notif-bell') as HTMLElement | null;
  if (!bell || !_currentUser) return;
  const badge = (bell as any)._tnBadge || bell.querySelector<HTMLSpanElement>('.tn-notif-badge');
  if (!badge) return;
  doRefresh(badge, _currentUser);
}
